import React, {useEffect, useMemo, useState} from "react"
import {BasePopup} from "components/popup"
import styles from "./LedgerCardPopup.module.css"
import {useAllAvailableStudentStatuses, useModel, useMutation, useShowEnrollmentIdField} from "hooks"
import {BaseButton} from "components"
import {Branding} from "types/branding"
import {convertImgToBase64, formatCurrency, getFullName, handleError, toastSuccess} from "helpers"
import {StudentTransaction, StudentTransactionType} from "types/student-account/student-ledger"
import moment from "moment"
import {Auth} from "types/auth"
import cx from "classnames"
import {PAYMENT_METHOD_OPTIONS} from "types/student-account/student-payment"
import {Col, Row} from "antd"
import {documentService, studentLedgerService} from "services"
import {BaseDepartmentId} from "types/departments"
import {roundCurrencyAmount} from "./student-transaction-balances"

type Props = {
    isShow: boolean
    onClose: () => void
    student?: Auth.DepartmentStudent
    transactions?: StudentTransaction[]
    showEnrollmentId?: boolean
}

export function LedgerCardPopup({isShow, onClose, student, transactions, showEnrollmentId = false}: Props) {
    const model = useModel()
    const {isVisible: showEnrollmentIdField, label: enrollmentIdLabel} = useShowEnrollmentIdField()
    const userDateFormat = model.getUserDateFormat()
    const userDateTimeFormat = model.getUserDateTimeFormat()
    const [isDownloading, setDownloading] = useState(false)
    const [logoBase64, setLogoBase64] = useState("")
    const {statuses} = useAllAvailableStudentStatuses()

    const visibleTransactions = useMemo(() => transactions?.filter((item) => item.id >= 0), [transactions])
    const showEnrollmentIdInTable = showEnrollmentId && showEnrollmentIdField
    const showEnrollmentIdInInfo = !showEnrollmentId && showEnrollmentIdField
    const {totalCharge, totalPayment} = useMemo(() => {
        let totalCharge = 0
        let totalPayment = 0
        visibleTransactions.forEach((transaction) => {
            switch (transaction.type) {
                case StudentTransactionType.Charge:
                    totalCharge += transaction.amount
                    break
                case StudentTransactionType.Payment:
                    totalPayment += transaction.amount
                    break
            }
        })
        return {totalCharge, totalPayment}
    }, [visibleTransactions])

    // the PDF is rendered by the backend (paginated, repeated table header)
    const generatePdf = React.useCallback(async (): Promise<Blob> => {
        const campus = student?.campuses?.[0]
        const hasSupportedLogo = /^data:image\/(png|jpe?g|gif|webp);base64,/.test(logoBase64)
        return await studentLedgerService.exportLedgerPdf({
            transactionIds: visibleTransactions.map((transaction) => transaction.tranxId),
            student: {
                fullName: getFullName(student),
                statusName: statuses.find((status) => status.statusId === student?.statusId)?.name,
                customProfileId: student?.customProfileId,
                address: student?.address,
                city: student?.city,
                state: student?.state,
                postalCode: student?.postalCode,
                startDate: student?.startDate,
                lastDayOfAttendance: student?.lastDayOfAttendance,
                graduationDate: student?.graduationDate,
                campusName: typeof campus === "string" ? campus : campus?.name,
                termName: student?.termName,
                programVersionName: student?.programVersionName
            },
            logoDataUrl: hasSupportedLogo ? logoBase64 : undefined,
            dateFormat: userDateFormat,
            dateTimeFormat: userDateTimeFormat,
            showEnrollmentIdInTable,
            showEnrollmentIdInInfo,
            enrollmentIdLabel
        })
    }, [
        student,
        statuses,
        logoBase64,
        visibleTransactions,
        userDateFormat,
        userDateTimeFormat,
        showEnrollmentIdInTable,
        showEnrollmentIdInInfo,
        enrollmentIdLabel
    ])

    const handleDownload = React.useCallback(async () => {
        setDownloading(true)
        try {
            const blob = await generatePdf()
            const url = URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.download = `Student Ledger Statement - ${getFullName(student)} - ${moment().format("YYYY-MM-DD")}.pdf`
            link.click()
            URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error)
        } finally {
            setDownloading(false)
        }
    }, [student, generatePdf])

    useEffect(() => {
        const logo = model.clientSetting?.branding?.[Branding.BrandingType.OfficialLogoSquare]
        if (logo) {
            convertImgToBase64(`${logo}?t=${Date.now()}`).then(setLogoBase64)
        }
    }, [model.clientSetting?.branding])

    // add mutation to save pdf to user document
    const {isLoading: isSavingDocument, mutate: saveDocument} = useMutation(
        async () => {
            const pdf = await generatePdf()
            const title = `Student Ledger Statement - ${getFullName(student)} - ${moment().format("YYYY-MM-DD")}`
            const fileName = `${title}.pdf`
            const file = new File([pdf], fileName, {
                type: "application/pdf",
                lastModified: Date.now()
            })

            // send PDF to student's documents
            const formData = new FormData()
            formData.append("document", file)
            const documentUrl = await documentService.uploadDocument(formData)
            await documentService.createDocument({
                documentUrl,
                code: student.profileId.toString(),
                departmentSubunitId: BaseDepartmentId.StudentAccount,
                name: title,
                description: title,
                isVisibleForUser: true,
                ownerUserProfileId: student.profileId,
                ownerUserId: student.userId
            })
        },
        {
            onSuccess: () => {
                toastSuccess("Document saved successfully")
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="DOLLAR_SIGN" leftIconColor="#fff" width="fit-content">
            <div className={styles.container}>
                <Row gutter={16} justify="end" className={styles.downloadButton}>
                    <Col>
                        <BaseButton loading={isDownloading} onClick={handleDownload} title={"Download"} />
                    </Col>

                    <Col>
                        <BaseButton
                            loading={isSavingDocument}
                            onClick={saveDocument}
                            title={"Save to User Documents"}
                        />
                    </Col>
                </Row>

                <div className={styles.pdfPage}>
                    <div className={styles.titleSection}>
                        <img className={styles.logo} alt="" src={logoBase64} />
                        <div>
                            <h1 className={styles.pageTitle}>Student Ledger / Statement</h1>
                        </div>
                        <div>{moment().format(userDateTimeFormat)}</div>
                    </div>

                    <div className={styles.studentSection}>
                        <div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Student Name</div>
                                <div className={styles.studentValue}>
                                    <b>{getFullName(student)}</b>
                                </div>
                            </div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Student Status</div>
                                <div className={styles.studentValue}>
                                    <b>{statuses.find((status) => status.statusId === student?.statusId)?.name}</b>
                                </div>
                            </div>
                            {showEnrollmentIdInInfo && (
                                <div className={styles.studentRow}>
                                    <div className={styles.studentLabel}>{enrollmentIdLabel}</div>
                                    <div className={styles.studentValue}>{student?.customProfileId}</div>
                                </div>
                            )}
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Address</div>
                                <div className={styles.studentValue}>
                                    {student?.address}
                                    <br />
                                    {student?.city}, {student?.state} {student?.postalCode}
                                </div>
                            </div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Start Date</div>
                                <div className={styles.studentValue}>
                                    {student?.startDate ? moment.utc(student.startDate).format(userDateFormat) : ""}
                                </div>
                            </div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>LDA</div>
                                <div className={styles.studentValue}>
                                    {student?.lastDayOfAttendance
                                        ? moment.utc(student.lastDayOfAttendance).format(userDateFormat)
                                        : ""}
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Balance</div>
                                <div className={styles.studentValue}>
                                    {formatCurrency(roundCurrencyAmount(totalCharge - totalPayment))}
                                </div>
                            </div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Campus</div>
                                <div className={styles.studentValue}>{student?.campuses?.[0]}</div>
                            </div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Current Term</div>
                                <div className={styles.studentValue}>{student?.termName}</div>
                            </div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Program Version</div>
                                <div className={styles.studentValue}>{student?.programVersionName}</div>
                            </div>
                            <div className={styles.studentRow}>
                                <div className={styles.studentLabel}>Graduation Date</div>
                                <div className={styles.studentValue}>
                                    {student?.graduationDate
                                        ? moment.utc(student.graduationDate).format(userDateFormat)
                                        : ""}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    {showEnrollmentIdInTable && <th className={styles.tableHeaderText}>Enr. ID</th>}
                                    <th className={styles.tableHeaderText}>Date</th>
                                    <th className={styles.tableHeaderText}>Receipt/Ref</th>
                                    <th className={styles.tableHeaderText}>Payment Method</th>
                                    <th className={styles.tableHeaderText}>Type</th>
                                    <th className={styles.tableHeaderText}>Description</th>
                                    <th className={cx(styles.tableHeaderText, styles.money)}>Charge</th>
                                    <th className={cx(styles.tableHeaderText, styles.money)}>Payment</th>
                                    <th className={cx(styles.tableHeaderText, styles.money)}>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleTransactions.map((transaction) => {
                                    const amount = formatCurrency(transaction.amount)
                                    const balance =
                                        transaction.balance != null
                                            ? formatCurrency(roundCurrencyAmount(transaction.balance))
                                            : ""
                                    let description = ""
                                    let transactionType = ""
                                    let receiptRef = ""
                                    switch (transaction.type) {
                                        case StudentTransactionType.Charge:
                                            description = transaction.charge?.description || ""
                                            transactionType =
                                                transaction.charge?.account?.name ||
                                                transaction.charge?.customAccountName
                                            receiptRef = transaction.charge?.notes
                                            break
                                        case StudentTransactionType.Payment:
                                            description = transaction.payment?.extraDescription || ""
                                            transactionType = transaction.payment?.account?.name
                                            receiptRef = transaction.payment?.receiptId
                                            break
                                    }
                                    return (
                                        <tr key={transaction.tranxId}>
                                            {showEnrollmentIdInTable && (
                                                <td className={styles.tableColumn}>
                                                    {transaction.profile?.customProfileId || ""}
                                                </td>
                                            )}
                                            <td className={styles.tableColumn}>
                                                {moment.utc(transaction.tranxDate).format(userDateFormat)}
                                            </td>
                                            <td
                                                className={cx(styles.tableColumn, styles.receiptText)}
                                                title={receiptRef}>
                                                {receiptRef}
                                            </td>
                                            <td className={styles.tableColumn}>
                                                {PAYMENT_METHOD_OPTIONS.find(
                                                    (option) => option.id === transaction.payment?.methodOfPayment
                                                )?.name || transaction.payment?.methodOfPayment}
                                            </td>
                                            <td className={styles.tableColumn}>{transactionType}</td>
                                            <td className={styles.tableColumn}>{description}</td>

                                            <td className={cx(styles.tableColumn, styles.money)}>
                                                {transaction.type === StudentTransactionType.Charge && amount}
                                            </td>
                                            <td className={cx(styles.tableColumn, styles.money)}>
                                                {transaction.type === StudentTransactionType.Payment && (
                                                    <div>
                                                        <div>{amount}</div>
                                                        {/*!transaction.payment?.postedDate && (
                                                            <div className={styles.descText}>
                                                                <i>Processing</i>
                                                            </div>
                                                        )*/}
                                                    </div>
                                                )}
                                            </td>
                                            <td className={cx(styles.tableColumn, styles.money)}>{balance}</td>
                                        </tr>
                                    )
                                })}
                                <tr className={styles.totalRow}>
                                    {showEnrollmentIdInTable && <td className={styles.tableColumn} />}
                                    <td className={styles.tableColumn} />
                                    <td className={styles.tableColumn} />
                                    <td className={styles.tableColumn} />
                                    <td className={styles.tableColumn} />
                                    <td className={styles.tableColumn}>
                                        <b>Total</b>
                                    </td>
                                    <td className={cx(styles.tableColumn, styles.money)}>
                                        <b>{formatCurrency(totalCharge)}</b>
                                    </td>
                                    <td className={cx(styles.tableColumn, styles.money)}>
                                        <b>{formatCurrency(totalPayment)}</b>
                                    </td>
                                    <td className={cx(styles.tableColumn, styles.money)} />
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </BasePopup>
    )
}
